import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography,
  AlertColor,
  ListItemButton,
  Box,
  CircularProgress,
  Divider,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { getJwtTokenFromIdToken } from '../auth/AuthFanc';
import { fetchErrorText } from '../../../common/const';
import { useParams } from 'react-router-dom';
import ConfirmDialog from '../../molecules/ConfirmDialog';

export type Sdc = {
  doorPlace: string | null;
  sdcUuid: string;
  sdcSerialNumber: string;
  memo: string;
  shippingDate: string | null;
};

type ValidationError = {
  dstSdc: string;
};

const emptyValidationError: ValidationError = {
  dstSdc: ''
}

export type NewSdcReplacementModalProps = {
  open: boolean;
  close: () => void;
  sourceSdc: Sdc;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export const NewSdcReplacementModal: React.FC<NewSdcReplacementModalProps> = (
  props: NewSdcReplacementModalProps
) => {
  const { autoBuildingId } = useParams();

  const [isSdcsLoading, setIsSdcsLoading] = React.useState<boolean>(false);
  const [sdcs, setSdcs] = React.useState<Sdc[]>([]);
  const [dstSdc, setDstSdc] = React.useState<Sdc | null>(null);

  const [memo, setMemo] = React.useState<string>('');

  const [validationErrors, setValidationErrors] = React.useState<ValidationError>(
    emptyValidationError
  );

  const [openConfirm, setOpenConfirm] = React.useState<boolean>(false);

  const [isSubmitInProgress, setIsSubmitInProgress] =
    React.useState<boolean>(false);

  const handleValidation = () => {
    const errors = {
      dstSdc: dstSdc === null ? `交換先のSDCを選択してください。` : '',
    };
    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error !== '');
  };

  const fetchFreeSdcs = async () => {
    setIsSdcsLoading(true);
    await getJwtTokenFromIdToken()
      .then((token) => {
        // call indexSdcs API
        return fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/sdcs?isRegisteredAsDoor=false`,
          {
            method: 'GET',
            headers: {
              Authorization: token,
            },
          }
        );
      })
      .then((response) => {
        if (!response.ok) {
          console.error(`(indexSdcs) HTTP error! status: ${response.status}`);
          props.onError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((body) => {
        if (!body) {
          return;
        }

        // SDC出荷日のフィルタリング
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(0, 0, 0, 0);

        const filteredSdcs = body.sdcs.filter((sdc: any) => {
          // 出荷日が未設定の場合は除外
          if (!sdc.shippingDate || sdc.shippingDate === "0000-00-00 00:00:00") {
            return false;
          }

          // 出荷日が明日以降の場合は表示しない
          const shippingDate = new Date(sdc.shippingDate);
          shippingDate.setHours(0, 0, 0, 0);
          return shippingDate < tomorrow; // 明日より前（今日まで）の出荷日のみ表示
        });

        setSdcs(filteredSdcs);
      })
      .catch((e) => {
        console.error(e);
        props.onError(fetchErrorText);
      })
      .finally(() => {
        setIsSdcsLoading(false);
      });
  };

  const handleClose = () => {
    // reset useState variables to reset the view on next time opened
    setDstSdc(null);
    setMemo('');
    setValidationErrors(emptyValidationError);

    // close this modal
    props.close();
  };

  const handleSubmit = async () => {
    setIsSubmitInProgress(true);

    await getJwtTokenFromIdToken()
      .then((token) => {
        // call createSdcReplacement API
        return fetch(
          `${process.env.REACT_APP_API_ENDPOINT}/buildings/` +
            `${autoBuildingId}/sdc-replacements`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: token,
            },
            body: JSON.stringify({
              srcSdcUuid: props.sourceSdc.sdcUuid,
              dstSdcUuid: dstSdc!.sdcUuid,
              memo: memo,
            }),
          }
        );
      })
      .then((response) => {
        if (!response.ok) {
          console.error(
            `(createSdcReplacement) HTTP error! status: ${response.status}`
          );
          props.onError(fetchErrorText);
          return null;
        }
        return response.json();
      })
      .then((body) => {
        if (body !== null) {
          // show confirm dialog and reload page
          window.alert('SDC交換を保存しました。');
          window.location.reload();
        }
      })
      .catch((e) => {
        console.error(e);
        props.onError(fetchErrorText);
      })
      .finally(() => {
        setIsSubmitInProgress(false);
      });
  };

  React.useEffect(() => {
    if (props.open) {
      // go fetch list of SDCs not registered to any building
      fetchFreeSdcs();
    }
  }, [props.open]);

  return (
    <Dialog open={props.open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>SDC交換</DialogTitle>
      <DialogContent>
        <Box>
          <Typography variant="body1">
            {
              'SDC交換を実施すると、交換元SDCは物件から削除され、交換先SDCが' +
              '物件に登録されます。'
            }
            <br />
            {
              'また、交換元SDCの設定値はすべて交換先SDCへコピーされ、' +
              'FTスマートキー鍵などのSDCに紐づくリソースは移管（＝交換元SDCから削除）' +
              'されます。'
            }
          </Typography>
          <Typography variant='body1' color='red'>
            実行前によくお確かめください。
          </Typography>
        </Box>
        <Divider sx={{ my: 1 }} />
        <Stack direction='column' spacing={2}>
          
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              SDC（ドア）設置場所
            </Typography>
            <Box sx={{ ml: 5 }}>
              <Typography variant="body2">{props.sourceSdc.doorPlace}</Typography>
            </Box>
          </Box>
          <Box>
            {/* source SDC Box */}
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              交換元SDC
            </Typography>
            <Box sx={{ ml: 5 }}>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">シリアルナンバー：</Typography>
                  <Typography variant="body2">UUID：</Typography>
                  <Typography variant="body2">メモ：</Typography>
                </Box>
                <Box sx={{ flexGrow: 4 }}>
                  <Typography variant="body2" fontFamily="monospace">
                    {props.sourceSdc.sdcSerialNumber}
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {props.sourceSdc.sdcUuid}
                  </Typography>
                  <Typography variant="body2">{props.sourceSdc.memo}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box>
            {/* destination SDC Box */}
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              交換先SDC
            </Typography>
            <Box sx={{ ml: 5 }}>
              <Box>
                {/* SDC selection Box */}
                {isSdcsLoading ? (
                  <CircularProgress size={40} />
                ) : (
                  <Autocomplete
                    options={sdcs}
                    getOptionLabel={(option) => {
                      // 出荷日の表示を追加
                      const shippingDate = option.shippingDate 
                        ? new Date(option.shippingDate).toLocaleDateString('ja-JP')
                        : '未設定';
                      return `${option.sdcSerialNumber} (出荷日: ${shippingDate})`;
                    }}
                    style={{ width: 400 }}
                    size="small"
                    onChange={(event, newValue) => {
                      if (newValue) {
                        setDstSdc(newValue);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="SDCシリアルナンバー検索"
                        variant="outlined"
                      />
                    )}
                  />
                )}
                <Typography variant="body2" color="red">
                  {validationErrors.dstSdc}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="body2">シリアルナンバー：</Typography>
                  <Typography variant="body2">UUID：</Typography>
                  <Typography variant="body2">メモ：</Typography>
                </Box>
                <Box sx={{ flexGrow: 4 }}>
                  <Typography variant="body2" fontFamily="monospace">
                    {dstSdc?.sdcSerialNumber ?? '-'}
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace">
                    {dstSdc?.sdcUuid ?? '-'}
                  </Typography>
                  <Typography variant="body2">{dstSdc?.memo ?? '-'}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
              交換メモ
            </Typography>
            <Box sx={{ ml: 5 }}>
              <TextField
                size="small"
                style={{width: 400}}
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={isSubmitInProgress} onClick={handleClose}>
          キャンセル
        </Button>
        <Button
          disabled={isSubmitInProgress}
          onClick={() => {
            if (!handleValidation()) {
              return;
            }
            setOpenConfirm(true);
          }}
        >
          {isSubmitInProgress ? <CircularProgress size={24} /> : '実行'}
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={openConfirm}
        onClose={() => {
          setOpenConfirm(false);
        }}
        onConfirm={handleSubmit}
        title="確認"
        description={'本当にSDC交換を実施しますか？'}
        showCancelButton={true}
      />
    </Dialog>
  );
};
